/**
 * Tell the Capgo updater this bundle booted successfully (Card 13).
 *
 * THIS IS THE AUTO-ROLLBACK CONTRACT, AND IT IS NOT OPTIONAL. After the plugin installs an OTA
 * bundle it starts a timer (`appReadyTimeout`, 20s in capacitor.config.ts). If `notifyAppReady()`
 * has not been called by then it reverts to the last bundle that did call it. Forget this and
 * every update silently rolls back a few seconds after launch — which presents identically to
 * "OTA doesn't work", with nothing in any log to say otherwise.
 *
 * WHERE IT RUNS, AND WHY NOT EARLIER. Deliberately not a module side effect and not a bare
 * `useEffect(..., [])`: either would fire the moment the JS bundle is parsed, which proves only
 * that the file downloaded. It waits for two things instead:
 *
 *   1. React has mounted — the component tree rendered without throwing.
 *   2. The auth session has resolved (`useAuth().isLoading === false`) — the app reached the API
 *      and settled on a signed-in or signed-out state.
 *
 * (2) is what makes this a real health check rather than a formality. The native session
 * credential lives in the Keychain/Keystore behind `@aparajita/capacitor-secure-storage` and is
 * sent as a bearer token, so an OTA bundle that changes the auth transport against a binary
 * without the matching native plugin logs every user out — the exact failure the card's note 2
 * warns about. Gating on session resolution means that bundle never gets marked healthy, and the
 * device rolls back on its own.
 *
 * The flip side is the reason `appReadyTimeout` is raised to 20s: a cold start on a slow mobile
 * connection can spend most of the default 10s inside that first `/api/auth/me`.
 *
 * No-op on web and in the CAP_ENV=beta remote-URL wrapper — there is no installed bundle to
 * confirm — and the plugin's own web shim makes the call harmless anyway.
 */

import { useEffect, useRef } from 'react';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { useAuth } from '@/contexts/AuthContext';
import { isNative } from '@/utils/platform';

/**
 * How long to wait for the session before reporting ready anyway.
 *
 * Readiness used to wait on the session with no bound, which meant the plugin's `appReadyTimeout`
 * had to cover the worst cold start on bad mobile data — 20s — and a broken bundle therefore took
 * 20s to roll back. Bounding the wait here is what lets that come down to 10s: a healthy bundle
 * now always reports within ~4s of mounting, whatever the network is doing.
 *
 * What this gives up: a bundle whose session request HANGS past 4s is now marked healthy instead
 * of being rolled back. That is a deliberate trade and a small one — the failure this net exists
 * to catch is a bundle that cannot run at all, and that is caught by React never mounting, which
 * is unaffected. A hung request is also indistinguishable from a slow one, which is precisely why
 * gating on it was making healthy bundles look broken.
 */
const SESSION_WAIT_MS = 4000;

export function useOtaAppReady(): void {
  const { isLoading } = useAuth();
  // Once per app run. `notifyAppReady` is idempotent on the native side, but StrictMode
  // double-invokes effects in development and a duplicate call would muddy the plugin's logs.
  const notified = useRef(false);

  useEffect(() => {
    if (!isNative) return;
    if (notified.current) return;

    const report = (via: 'session-resolved' | 'timeout') => {
      if (notified.current) return;
      notified.current = true;

      CapacitorUpdater.notifyAppReady()
        .then((result) => {
          console.log(`[OTA] Bundle confirmed healthy (${via}):`, result.bundle.version);
        })
        .catch((error) => {
          // Reaching here means the plugin is absent from this binary — an OTA bundle running on
          // a build that predates Card 13. Log loudly rather than swallowing: there is no
          // rollback protection at all in that state, and it is the failure mode
          // `verify-updater-linked.mjs` exists to keep out of shipped artifacts.
          console.error('[OTA] notifyAppReady() failed — this build has no working updater:', error);
        });
    };

    // React having mounted is already proven by this effect running at all. Report as soon as the
    // session settles, or after SESSION_WAIT_MS regardless — whichever comes first.
    if (!isLoading) {
      report('session-resolved');
      return;
    }

    const timer = setTimeout(() => {
      console.warn('[OTA] session still resolving; reporting ready to avoid a false rollback');
      report('timeout');
    }, SESSION_WAIT_MS);

    return () => clearTimeout(timer);
  }, [isLoading]);
}
